import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  MessageSquare, 
  Send, 
  Bot, 
  User, 
  Loader2, 
  ChevronLeft,
  HelpCircle,
  Calculator,
  Users,
  Mic,
  CreditCard,
  Settings,
  Building2
} from 'lucide-react';
import AIConsentModal from '../components/AIConsentModal';
import { hasAIConsent } from '../lib/aiConsent';

const FAQPage = () => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Bonjour ! Je suis l'assistant virtuel du Club 10 Millions Investor. 👋\n\nJe suis là pour répondre à toutes vos questions sur l'utilisation de notre plateforme : calculateur de rentabilité, Agent IA, communauté, abonnements, et bien plus.\n\nComment puis-je vous aider aujourd'hui ?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [needsAIConsent, setNeedsAIConsent] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!hasAIConsent()) setNeedsAIConsent(true);
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    if (!hasAIConsent()) {
      setNeedsAIConsent(true);
      return;
    }

    const userMessage = input.trim();
    setInput('');
    
    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/faq/ask`, {
        question: userMessage,
        conversation_history: messages.map(m => ({ role: m.role, content: m.content }))
      });

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.answer 
      }]);
    } catch (error) {
      console.error('FAQ Error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Désolé, je rencontre un problème technique. Vous pouvez contacter notre support à bonjour@10millionsinvestor.com pour obtenir de l'aide." 
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const quickQuestions = [
    { icon: Calculator, text: "Comment fonctionne le calculateur ?", question: "Comment fonctionne le calculateur de rentabilité ?" },
    { icon: Bot, text: "Qu'est-ce que l'Agent IA ?", question: "Qu'est-ce que l'Agent IA Immobilier et comment l'utiliser ?" },
    { icon: CreditCard, text: "Quels sont les tarifs ?", question: "Quels sont les différents abonnements et leurs tarifs ?" },
    { icon: Users, text: "Comment rejoindre le Club ?", question: "Comment rejoindre le Club 10M Investor ?" },
    { icon: Mic, text: "Accès aux podcasts ?", question: "Comment accéder aux podcasts exclusifs ?" },
    { icon: Building2, text: "Publier un bien ?", question: "Comment publier un bien immobilier sur la plateforme ?" },
  ];

  const handleQuickQuestion = (question) => {
    setInput(question);
    inputRef.current?.focus();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <AIConsentModal
        open={needsAIConsent}
        onAccept={() => setNeedsAIConsent(false)}
        onDecline={() => setNeedsAIConsent(false)}
      />
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Logo size="small" linkTo="/" />
            <Link to="/">
              <Button variant="outline" size="sm">
                <ChevronLeft className="h-4 w-4 mr-1" />
                Retour
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#0D47A1] to-[#00B050] rounded-2xl mb-4">
            <HelpCircle className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Centre d'Aide</h1>
          <p className="text-slate-600">Posez vos questions à notre assistant IA</p>
        </div>

        {/* Chat Container */}
        <Card className="border-0 shadow-xl overflow-hidden">
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-[#0D47A1] to-[#1565C0] p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-white font-semibold">Assistant 10M Investor</h2>
                <p className="text-white/70 text-sm">En ligne • Répond instantanément</p>
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <CardContent className="p-0">
            <ScrollArea className="h-[400px] p-4" ref={scrollRef}>
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`flex gap-3 max-w-[85%] ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        message.role === 'user' 
                          ? 'bg-[#0D47A1]' 
                          : 'bg-gradient-to-br from-[#0D47A1] to-[#00B050]'
                      }`}>
                        {message.role === 'user' 
                          ? <User className="h-4 w-4 text-white" />
                          : <Bot className="h-4 w-4 text-white" />
                        }
                      </div>
                      <div className={`rounded-2xl px-4 py-3 ${
                        message.role === 'user'
                          ? 'bg-[#0D47A1] text-white rounded-br-md'
                          : 'bg-slate-100 text-slate-800 rounded-bl-md'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Loading indicator */}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="flex gap-3 max-w-[85%]">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#0D47A1] to-[#00B050] flex items-center justify-center flex-shrink-0">
                        <Bot className="h-4 w-4 text-white" />
                      </div>
                      <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin text-[#0D47A1]" />
                          <span className="text-sm text-slate-500">En train de répondre...</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Quick Questions */}
            <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50">
              <p className="text-xs text-slate-500 mb-2">Questions fréquentes :</p>
              <div className="flex flex-wrap gap-2">
                {quickQuestions.map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickQuestion(q.question)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-xs text-slate-600 hover:bg-slate-50 hover:border-[#0D47A1] hover:text-[#0D47A1] transition-colors"
                  >
                    <q.icon className="h-3 w-3" />
                    {q.text}
                  </button>
                ))}
              </div>
            </div>

            {/* Input Area */}
            <form onSubmit={handleSubmit} className="p-4 border-t border-slate-200 bg-white">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Posez votre question..."
                  className="flex-1"
                  disabled={isLoading}
                  data-testid="faq-input"
                />
                <Button 
                  type="submit" 
                  disabled={!input.trim() || isLoading}
                  className="bg-[#0D47A1] hover:bg-[#0A367A]"
                  data-testid="faq-send-button"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Additional Help */}
        <div className="mt-8 text-center">
          <p className="text-sm text-slate-500 mb-4">
            Vous n'avez pas trouvé la réponse à votre question ?
          </p>
          <Link to="/contact">
            <Button variant="outline" className="border-[#0D47A1] text-[#0D47A1] hover:bg-[#0D47A1] hover:text-white">
              <MessageSquare className="h-4 w-4 mr-2" />
              Contacter le support
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default FAQPage;
